import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Typography,
  Select,
  DatePicker,
  Button,
  Statistic,
  Tag,
  Timeline,
  message,
  Spin,
  Modal,
  Tooltip,
  Collapse,
  Divider,
  Space,
  Alert,
} from "antd";
import { Line } from "react-chartjs-2";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  BookOutlined,
  VideoCameraOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";
import { processDirectusLoan } from "../utils/loanUtils";

// Custom CSS for tour highlight animation
const tourStyles = `
  @keyframes pulse-border {
    0% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(24, 144, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0);
    }
  }
  
  .tour-highlight {
    animation: pulse-border 2s infinite;
    border-radius: 4px;
    transition: all 0.3s ease-in-out;
  }
`;

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const CashFlowForecast = () => {
  const { company } = useContext(AppContext);
  const [range, setRange] = useState("3M");
  const token = localStorage.getItem("token");
  const [loanRepayments, setLoanRepayments] = useState(0);
  const [percentageChange, setPercentageChange] = useState(0);
  const [previousMonthInflow, setPreviousMonthInflow] = useState(0);
  const [outflowsNext30Days, setOutflowsNext30Days] = useState(0);
  const [bufferDays, setBufferDays] = useState(0);
  const [projectedBalance, setProjectedBalance] = useState(0);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [helpVisible, setHelpVisible] = useState(false);
  const [guideTipVisible, setGuideTipVisible] = useState(true);
  const [activeTourStep, setActiveTourStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [showTip, setShowTip] = useState(null);

  const periodEndDate = moment()
    .add(
      parseInt(range.replace("M", "").replace("Y", "")),
      range.includes("M") ? "months" : "years"
    )
    .format("MMM YYYY");

  // Tour steps for interactive guide
  const tourSteps = [
    {
      title: "Cash Flow Forecast",
      content:
        "This tool helps you predict your future cash position based on loan schedules and transactions.",
      target: ".forecast-header",
    },
    {
      title: "Forecast Period",
      content:
        "Change the time range to see projections for different periods.",
      target: ".forecast-period",
    },
    {
      title: "Key Metrics",
      content:
        "These cards show your most important cash flow information for the next 30 days.",
      target: ".key-metrics",
    },
    {
      title: "Cash Flow Chart",
      content: "This chart shows your projected cash balance over time.",
      target: ".cash-flow-chart",
    },
    {
      title: "Monthly Projections",
      content: "This table provides detailed monthly cash flow data.",
      target: ".monthly-projections",
    },
  ];

  // Function to generate cash flow data
  const generateCashFlowData = (loans, transactions) => {
    const months = [];
    let currentMonth = moment();

    // Determine number of months based on range
    const monthsToGenerate = range.includes("M")
      ? parseInt(range.replace("M", ""))
      : parseInt(range.replace("Y", "")) * 12;

    // Initialize with current month
    let lastBalance = company?.balance || 0;

    for (let i = 0; i < monthsToGenerate; i++) {
      const monthDate = currentMonth.clone().add(i, "months");
      const monthLabel = monthDate.format("MMM YYYY");
      const monthStart = monthDate.clone().startOf("month");
      const monthEnd = monthDate.clone().endOf("month");

      // Calculate inflows: expected loan repayments in this month
      const expectedRepayments = loans
        .filter((loan) => {
          const nextPaymentDate = moment(loan.next_payment_date);
          return nextPaymentDate.isBetween(monthStart, monthEnd, null, "[]");
        })
        .reduce((sum, loan) => sum + (loan.next_payment_amount || 0), 0);

      // Calculate additional inflows from recurring transactions
      const otherInflows = transactions
        .filter(
          (tx) =>
            tx.transaction_type === "Loan Repayment" &&
            moment(tx.scheduled_date || tx.transaction_date).isBetween(
              monthStart,
              monthEnd,
              null,
              "[]"
            )
        )
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      // Total inflows
      const totalInflows = expectedRepayments + otherInflows;

      // Calculate outflows: expected disbursements
      const expectedDisbursements = loans
        .filter((loan) => {
          // Loans expected to be approved and disbursed in this month
          const approvalDate = moment(
            loan.scheduled_approval_date || loan.approval_date
          );
          return (
            loan.status === "Pending Approval" &&
            approvalDate.isBetween(monthStart, monthEnd, null, "[]")
          );
        })
        .reduce((sum, loan) => sum + (loan.amount || 0), 0);

      // Calculate other outflows like operational expenses from transactions
      const otherOutflows = transactions
        .filter(
          (tx) =>
            tx.transaction_type === "Loan Disbursement" &&
            moment(tx.scheduled_date || tx.transaction_date).isBetween(
              monthStart,
              monthEnd,
              null,
              "[]"
            )
        )
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      // Total outflows
      const totalOutflows = expectedDisbursements + otherOutflows;

      // Calculate forecast balance
      const forecastBalance = lastBalance + totalInflows - totalOutflows;

      // Add to months array
      months.push({
        month: monthLabel,
        opening: lastBalance,
        inflows: totalInflows,
        outflows: totalOutflows,
        forecast: forecastBalance,
      });

      // Update last balance for next iteration
      lastBalance = forecastBalance;
    }

    return months;
  };

  // Chart data generator
  const generateChartData = (cashFlowData) => {
    return {
      labels: cashFlowData.map((d) => d.month),
      datasets: [
        {
          label: "Projected Balance",
          data: cashFlowData.map((d) => d.forecast),
          borderColor: "#1890ff",
          backgroundColor: "#1890ff33",
          tension: 0.4,
          fill: true,
        },
        {
          label: "Actual Balance",
          data: cashFlowData.map((d) => d.opening),
          borderColor: "#52c41a",
          borderDash: [5, 5],
          backgroundColor: "#52c41a33",
          tension: 0.4,
        },
      ],
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all necessary data in parallel
        const [
          loansResponse,
          transactionsResponse,
          historicalTransactionsResponse,
        ] = await Promise.all([
          // Active loans
          axios.get(
            `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company?.id}&filter[status][_in]=Active,Pending Approval`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),

          // All transactions (loan repayments, expenses, etc.)
          axios.get(
            `https://zedlenders.pickmesms.com/items/transactions?filter[company]=${company?.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),

          // Historical transactions for previous months
          axios.get(
            `https://zedlenders.pickmesms.com/items/transactions?filter[company]=${
              company?.id
            }&filter[transaction_date][_between]=${moment()
              .subtract(2, "months")
              .format("YYYY-MM-DD")},${moment()
              .subtract(1, "month")
              .format("YYYY-MM-DD")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        // Process loans with proper calculations
        const loans = loansResponse.data.data.map((loan) => {
          // Use existing utility or enrich with needed properties
          return processDirectusLoan(loan);
        });

        // Get all transactions
        const transactions = transactionsResponse.data.data || [];

        // Calculate inflow from previous month for comparison
        const previousMonthInflows = historicalTransactionsResponse.data.data
          .filter((tx) => tx.transaction_type === "Loan Repayment")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);

        setPreviousMonthInflow(previousMonthInflows);

        // Define time periods
        const today = moment();
        const next30Days = moment().add(30, "days");

        // Calculate upcoming repayments for next 30 days
        const upcomingRepayments = loans
          .filter((loan) =>
            moment(loan.next_payment_date).isBetween(
              today,
              next30Days,
              null,
              "[]"
            )
          )
          .reduce((sum, loan) => sum + (loan.next_payment_amount || 0), 0);

        // Calculate upcoming outflows for next 30 days (from transactions table)
        const upcomingOutflows = transactions
          .filter(
            (tx) =>
              tx.transaction_type === "Loan Disbursement" &&
              moment(tx.scheduled_date || tx.transaction_date).isBetween(
                today,
                next30Days,
                null,
                "[]"
              )
          )
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);

        // Add scheduled loan disbursements
        const upcomingDisbursements = loans
          .filter(
            (loan) =>
              loan.status === "Pending Approval" &&
              moment(
                loan.scheduled_approval_date || loan.approval_date
              ).isBetween(today, next30Days, null, "[]")
          )
          .reduce((sum, loan) => sum + (loan.amount || 0), 0);

        // Set state values
        setLoanRepayments(upcomingRepayments);
        setOutflowsNext30Days(upcomingOutflows + upcomingDisbursements);

        // Calculate percentage change
        const pChange =
          previousMonthInflows > 0
            ? (
                ((upcomingRepayments - previousMonthInflows) /
                  previousMonthInflows) *
                100
              ).toFixed(1)
            : 0;

        setPercentageChange(pChange);

        // Calculate buffer days
        const currentCashReserves = company?.balance || 0;
        const totalOutflow = upcomingOutflows + upcomingDisbursements;
        const averageDailyOutflow = totalOutflow > 0 ? totalOutflow / 30 : 1;
        const buffer = currentCashReserves / averageDailyOutflow;

        setBufferDays(Math.round(buffer));

        // Calculate projected balance
        const projected =
          currentCashReserves + upcomingRepayments - totalOutflow;
        setProjectedBalance(projected);

        // Generate cash flow data for chart and table
        const cashFlowData = generateCashFlowData(loans, transactions);
        setCashFlowData(cashFlowData);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load cash flow data");
        setLoading(false);
      }
    };

    if (company?.id && token) {
      fetchData();
    }
  }, [company?.id, token, range]);

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      sorter: (a, b) => a.month.localeCompare(b.month),
    },
    {
      title: "Opening Balance",
      dataIndex: "opening",
      render: (val) => <Text strong>ZMW {val.toLocaleString()}</Text>,
      sorter: (a, b) => a.opening - b.opening,
    },
    {
      title: "Inflows",
      dataIndex: "inflows",
      render: (val) => (
        <div>
          <ArrowUpOutlined style={{ color: "#52c41a" }} />
          <Text style={{ color: "#52c41a", marginLeft: 8 }}>
            ZMW {val.toLocaleString()}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.inflows - b.inflows,
    },
    {
      title: "Outflows",
      dataIndex: "outflows",
      render: (val) => (
        <div>
          <ArrowDownOutlined style={{ color: "#ff4d4f" }} />
          <Text style={{ color: "#ff4d4f", marginLeft: 8 }}>
            ZMW {val.toLocaleString()}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.outflows - b.outflows,
    },
    {
      title: "Projected Closing",
      dataIndex: "forecast",
      render: (val, record) => (
        <div>
          <Text strong>ZMW {val.toLocaleString()}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={val > record.opening ? "green" : "red"}>
              {(((val - record.opening) / record.opening) * 100).toFixed(1)}%
            </Tag>
          </div>
        </div>
      ),
      sorter: (a, b) => a.forecast - b.forecast,
    },
  ];

  // Generate chart data
  const chartData = generateChartData(cashFlowData);

  // In-App Help Modal
  const renderHelpModal = () => {
    return (
      <Modal
        title={
          <div>
            <BookOutlined style={{ marginRight: 8 }} />
            Cash Flow Forecast Guide
          </div>
        }
        open={helpVisible}
        onCancel={() => setHelpVisible(false)}
        footer={[
          <Button key="video" type="default" icon={<VideoCameraOutlined />}>
            Watch Tutorial Video
          </Button>,
          <Button key="close" onClick={() => setHelpVisible(false)}>
            Close
          </Button>,
          <Button
            key="tour"
            type="primary"
            onClick={() => {
              setHelpVisible(false);
              setShowTour(true);
            }}
          >
            Take Interactive Tour
          </Button>,
        ]}
        width={800}
      >
        <Collapse defaultActiveKey={["1"]} expandIconPosition="right">
          <Panel header="Overview & Purpose" key="1">
            <Paragraph>
              The Cash Flow Forecast tool helps you predict your future cash
              position based on loan repayments, disbursements, and other
              financial transactions. Use this report to:
            </Paragraph>
            <ul>
              <li>Anticipate potential cash shortfalls</li>
              <li>Plan for loan disbursements</li>
              <li>Monitor your liquidity buffer</li>
              <li>Make informed financial decisions</li>
            </ul>
          </Panel>

          <Panel header="Understanding Key Metrics" key="2">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="Next 30 Days Inflow">
                  <Text>
                    Shows expected money coming in over the next 30 days,
                    primarily from loan repayments.
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="green">↑ Positive trend</Tag>
                    <Tag color="red">↓ Negative trend</Tag>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Next 30 Days Outflow">
                  <Text>
                    Shows expected money going out over the next 30 days,
                    including pending loan disbursements and expenses.
                  </Text>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Liquidity Buffer">
                  <Text>
                    Shows how many days your company could operate without any
                    new income.
                  </Text>
                  <Paragraph style={{ marginTop: 8 }}>
                    <Tag color="red">{"<30 days"}: Critical</Tag>
                    <Tag color="orange">30-60 days: Cautionary</Tag>
                    <Tag color="green">{">60 days"}: Healthy</Tag>
                  </Paragraph>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Projected Balance">
                  <Text>
                    Shows expected cash position at the end of your selected
                    period.
                  </Text>
                </Card>
              </Col>
            </Row>
          </Panel>

          <Panel header="Reading the Chart & Table" key="3">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="Cash Flow Trajectory Chart">
                  <ul style={{ paddingLeft: 16 }}>
                    <li>
                      <Text strong>Blue line:</Text> Projected Balance
                    </li>
                    <li>
                      <Text strong>Green dashed line:</Text> Actual Balance
                    </li>
                    <li>Upward trend indicates improving liquidity</li>
                    <li>
                      Downward trend indicates potential cash flow challenges
                    </li>
                  </ul>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Monthly Projections Table">
                  <ul style={{ paddingLeft: 16 }}>
                    <li>
                      <Text strong>Opening Balance:</Text> Cash at start of
                      month
                    </li>
                    <li>
                      <Text strong>Inflows:</Text> Expected money coming in
                    </li>
                    <li>
                      <Text strong>Outflows:</Text> Expected money going out
                    </li>
                    <li>
                      <Text strong>Projected Closing:</Text> Expected
                      end-of-month balance
                    </li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </Panel>

          <Panel header="Best Practices" key="4">
            <Paragraph>
              For effective financial planning using this forecast:
            </Paragraph>
            <ul>
              <li>Review the forecast weekly to monitor changes</li>
              <li>
                Pay special attention to periods where the projected balance
                drops significantly
              </li>
              <li>
                Use the liquidity buffer metric to ensure you maintain adequate
                reserves
              </li>
              <li>
                Take proactive steps when potential cash flow issues are
                identified
              </li>
              <li>
                Regularly compare actual results with projections to improve
                forecast accuracy
              </li>
            </ul>
          </Panel>

          <Panel header="Data Sources & Accuracy" key="5">
            <Paragraph>This forecast uses real data from:</Paragraph>
            <ul>
              <li>Loan schedules and next payment dates from active loans</li>
              <li>Pending loan approvals and their scheduled dates</li>
              <li>Transaction history and scheduled future transactions</li>
            </ul>
            <Divider />
            <Text strong>Accuracy by timeframe:</Text>
            <ul>
              <li>Short-term (1-3 months): Generally 80-90% accurate</li>
              <li>Medium-term (4-6 months): Generally 60-80% accurate</li>
              <li>Long-term (7-12 months): Generally 40-60% accurate</li>
            </ul>
          </Panel>
        </Collapse>
      </Modal>
    );
  };

  // Effect to handle highlighting of tour elements
  useEffect(() => {
    // Create style element for the tour styles
    const styleEl = document.createElement("style");
    styleEl.id = "tour-highlight-styles";
    styleEl.innerHTML = tourStyles;
    document.head.appendChild(styleEl);

    if (showTour && tourSteps[activeTourStep]) {
      const targetSelector = tourSteps[activeTourStep].target;
      const targetElement = document.querySelector(targetSelector);

      // Remove any existing highlight classes
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });

      // Add highlight to current target element
      if (targetElement) {
        targetElement.classList.add("tour-highlight");
        targetElement.style.position = "relative";
        targetElement.style.zIndex = "1000";

        // Scroll element into view if needed
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      // Clean up highlights when tour ends
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });
    }

    // Clean up function for when component unmounts or effect re-runs
    return () => {
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });

      // Remove the style element
      const styleElement = document.getElementById("tour-highlight-styles");
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [showTour, activeTourStep]);

  // Interactive tour component
  const renderTourStep = () => {
    if (!showTour) return null;

    const currentStep = tourSteps[activeTourStep];

    return (
      <Modal
        title={currentStep.title}
        open={showTour}
        footer={[
          <Button
            key="back"
            onClick={() => setActiveTourStep(Math.max(0, activeTourStep - 1))}
            disabled={activeTourStep === 0}
          >
            Previous
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setShowTour(false);
              // Clean up is handled by the effect
            }}
          >
            Exit Tour
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={() => {
              if (activeTourStep === tourSteps.length - 1) {
                setShowTour(false);
                // Clean up is handled by the effect
                message.success(
                  "Tour completed! You can restart it anytime from the help menu."
                );
              } else {
                setActiveTourStep(activeTourStep + 1);
              }
            }}
          >
            {activeTourStep === tourSteps.length - 1 ? "Finish" : "Next"}
          </Button>,
        ]}
        maskClosable={false}
        closable={false}
        style={{
          position: "fixed",
          top: "auto",
          left: "auto",
          right: "20px",
          bottom: "20px",
          transform: "none",
        }}
        width={400}
      >
        <Paragraph>{currentStep.content}</Paragraph>
      </Modal>
    );
  };

  // Tooltip helper component for inline help
  const HelpTooltip = ({ title, children }) => (
    <Tooltip title={title} color="#108ee9">
      <span style={{ marginLeft: 4, cursor: "help" }}>
        {children || <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />}
      </span>
    </Tooltip>
  );

  return (
    <div style={{ padding: 24 }}>
      {guideTipVisible && (
        <Alert
          message="Cash Flow Forecast"
          description={
            <Space direction="vertical">
              <Text>
                This tool helps you predict your future cash position. Click the
                help button to learn more.
              </Text>
              <Button
                type="primary"
                size="small"
                onClick={() => setHelpVisible(true)}
              >
                View Guide
              </Button>
            </Space>
          }
          type="info"
          showIcon
          icon={<BulbOutlined />}
          closable
          onClose={() => setGuideTipVisible(false)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
          className="forecast-header"
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              <DollarOutlined /> Cash Flow Forecast
              <HelpTooltip title="View the complete Cash Flow Forecast guide">
                <BookOutlined
                  style={{
                    fontSize: 16,
                    marginLeft: 8,
                    color: "#1890ff",
                    cursor: "pointer",
                  }}
                  onClick={() => setHelpVisible(true)}
                />
              </HelpTooltip>
            </Title>
            <Text type="secondary">{company?.name} • Rolling Forecast</Text>
          </Col>
          <Col className="forecast-period">
            <Select
              value={range}
              onChange={setRange}
              style={{ width: 120 }}
              onMouseEnter={() => setShowTip("period")}
              onMouseLeave={() => setShowTip(null)}
            >
              <Option value="1M">1 Month</Option>
              <Option value="3M">3 Months</Option>
              <Option value="6M">6 Months</Option>
              <Option value="1Y">1 Year</Option>
            </Select>
            <Tooltip
              title="Take a guided tour of this page"
              visible={showTip === "tour"}
            >
              <Button
                style={{ marginLeft: 16 }}
                icon={<VideoCameraOutlined />}
                onClick={() => setShowTour(true)}
                onMouseEnter={() => setShowTip("tour")}
                onMouseLeave={() => setShowTip(null)}
              >
                Tour
              </Button>
            </Tooltip>
            <Button style={{ marginLeft: 8 }} icon={<FileExcelOutlined />}>
              Export
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 20 }}>Loading cash flow forecast...</div>
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <Row
              gutter={16}
              style={{ marginBottom: 24 }}
              className="key-metrics"
            >
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={
                      <span>
                        Next 30 Days Inflow
                        <HelpTooltip title="Total money coming in over the next 30 days" />
                      </span>
                    }
                    value={loanRepayments}
                    prefix="ZMW"
                    valueStyle={{ color: "#52c41a", fontSize: 20 }}
                    tooltip="Includes loan repayments expected in the next 30 days"
                  />
                  <Text type="secondary">
                    {percentageChange >= 0 ? "+" : ""}
                    {percentageChange}% vs previous month
                  </Text>
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={
                      <span>
                        Next 30 Days Outflow
                        <HelpTooltip title="Total money going out over the next 30 days" />
                      </span>
                    }
                    value={outflowsNext30Days}
                    prefix="ZMW"
                    valueStyle={{ color: "#ff4d4f", fontSize: 20 }}
                  />
                  <Text type="secondary">Based on scheduled payments</Text>
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={
                      <span>
                        Liquidity Buffer
                        <HelpTooltip title="How many days your company could operate without new income" />
                      </span>
                    }
                    value={bufferDays}
                    suffix="days"
                    valueStyle={{
                      color:
                        bufferDays < 30
                          ? "#ff4d4f"
                          : bufferDays < 60
                          ? "#fa8c16"
                          : "#1890ff",
                      fontSize: 20,
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{
                      color:
                        bufferDays < 30
                          ? "#ff4d4f"
                          : bufferDays < 60
                          ? "#fa8c16"
                          : "#52c41a",
                    }}
                  >
                    {bufferDays < 30
                      ? "Critical"
                      : bufferDays < 60
                      ? "Cautionary"
                      : "Healthy"}
                  </Text>
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={
                      <span>
                        Projected Balance
                        <HelpTooltip title="Expected cash position at the end of the selected period" />
                      </span>
                    }
                    value={projectedBalance}
                    prefix="ZMW"
                    valueStyle={{ color: "#722ed1", fontSize: 20 }}
                    tooltip="Current balance + Inflows - Outflows"
                  />
                  <Text type="secondary">
                    End of {range} period ({periodEndDate})
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* Main Chart */}
            <Card
              title={
                <span>
                  Cash Flow Trajectory
                  <HelpTooltip title="Blue line shows projected balance, green dashed line shows actual balance" />
                </span>
              }
              bordered={false}
              className="cash-flow-chart"
            >
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const label = context.dataset.label || "";
                          const value = context.parsed.y || 0;
                          return `${label}: ZMW ${value.toLocaleString()}`;
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => `ZMW ${value.toLocaleString()}`,
                      },
                    },
                  },
                }}
              />
            </Card>

            {/* Detailed Table */}
            <Card
              title={
                <span>
                  Monthly Projections
                  <HelpTooltip title="Detailed breakdown of cash flow by month" />
                </span>
              }
              bordered={false}
              style={{ marginTop: 24 }}
              className="monthly-projections"
            >
              <Table
                columns={columns}
                dataSource={cashFlowData}
                rowKey="month"
                pagination={false}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2}>
                        <Text strong style={{ color: "#52c41a" }}>
                          ZMW{" "}
                          {cashFlowData
                            .reduce((sum, d) => sum + d.inflows, 0)
                            .toLocaleString()}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text strong style={{ color: "#ff4d4f" }}>
                          ZMW{" "}
                          {cashFlowData
                            .reduce((sum, d) => sum + d.outflows, 0)
                            .toLocaleString()}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <Text strong>
                          ZMW{" "}
                          {cashFlowData.length > 0
                            ? cashFlowData[
                                cashFlowData.length - 1
                              ].forecast.toLocaleString()
                            : "0"}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>

            {/* Key Assumptions */}
            <Card
              title={
                <span>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Forecasting Assumptions
                </span>
              }
              bordered={false}
              style={{ marginTop: 24 }}
              extra={
                <Button type="link" onClick={() => setHelpVisible(true)}>
                  Learn more about the forecast
                </Button>
              }
            >
              <Timeline>
                <Timeline.Item color="green">
                  <Text strong>Loan Collection Rate:</Text> Based on actual
                  scheduled repayments
                </Timeline.Item>
                <Timeline.Item color="red">
                  <Text strong>Outflows:</Text> Based on scheduled loan
                  disbursements
                </Timeline.Item>
                <Timeline.Item color="blue">
                  <Text strong>Collection Period:</Text> Based on actual payment
                  dates from loans
                </Timeline.Item>
                <Timeline.Item color="orange">
                  <Text strong>Buffer Calculation:</Text> Current cash divided
                  by daily outflow rate
                </Timeline.Item>
              </Timeline>
            </Card>
          </>
        )}
      </Card>

      {/* Help Modal */}
      {renderHelpModal()}

      {/* Tour Component */}
      {renderTourStep()}
    </div>
  );
};

export default CashFlowForecast;
